import { useState } from "react";
import { motion } from "framer-motion";
import { Building2, Send, Loader2, FileCheck2, Timer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fade = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function B2B() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Enter your name / hospital");
    if (!/^\d{10}$/.test(form.phone)) return toast.error("Enter a valid 10-digit phone number");
    if (!form.message.trim()) return toast.error("Tell us briefly what you need");
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, type: "b2b" });
      toast.success("Request sent! We'll reach out within 24 hours.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="b2b" className="relative bg-[#0B192C] py-20 lg:py-28 overflow-hidden grain" data-testid="b2b-section">
      <div className="absolute inset-0 dot-grid opacity-60" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative z-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="flex items-center gap-4 mb-6">
            <span className="font-mono-med text-sm text-[#14B8A6] border border-[#14B8A6]/40 rounded-full px-3.5 py-1">07</span>
            <span className="text-xs font-semibold tracking-[0.25em] uppercase text-slate-400">For hospitals & labs</span>
            <span className="h-px flex-1 bg-white/10" />
          </motion.div>
          <motion.h2 variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
            Stop couriering hematopathology out of Vizag. <span className="text-[#D92638]">Send it to us.</span>
          </motion.h2>
          <motion.p variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            className="mt-5 text-sm lg:text-base text-slate-300 leading-relaxed max-w-xl">
            Reporting-as-a-service for hospitals, nursing homes and regional labs. Dispatch bone marrow and flow cytometry samples to Hemozy and receive specialist-signed reports in 24-48 hours - white-label friendly, priority handling for hemato-oncology cases.
          </motion.p>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mt-8 space-y-3.5">
            {[
              { icon: Timer, t: "24-48 hr specialist reports, not 3-7 days" },
              { icon: FileCheck2, t: "Signed by PDCC Hematopathology (CMC Vellore)" },
              { icon: ShieldCheck, t: "Cold-chain pickup & digital report delivery" },
            ].map(({ icon: Icon, t }) => (
              <div key={t} className="flex items-center gap-3.5 text-sm text-slate-200">
                <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-[#14B8A6]" />
                </div>
                {t}
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="lg:col-span-6">
          <form onSubmit={submit} className="bg-white rounded-[1.75rem] p-7 lg:p-9 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)]" data-testid="b2b-form">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-[#D92638]/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#D92638]" />
              </div>
              <div>
                <h3 className="font-bold text-[#0B192C]">Partner with Hemozy</h3>
                <p className="text-xs text-slate-500">Referral account setup in 24 hours</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Hospital / your name *" data-testid="b2b-name-input"
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} placeholder="Phone *" inputMode="numeric" data-testid="b2b-phone-input"
                  className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
              </div>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email" data-testid="b2b-email-input"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="What cases do you currently outsource? (bone marrow, flow cytometry, coagulation...) *" data-testid="b2b-message-input"
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
              <button type="submit" disabled={sending} data-testid="b2b-submit-button"
                className="w-full flex items-center justify-center gap-2 bg-[#D92638] hover:bg-[#B81D2D] text-white font-semibold py-3.5 rounded-full transition-all duration-200 active:scale-[0.98] disabled:opacity-60">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sending ? "Sending..." : "Request partnership"}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}
